import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import Timeline from "../../components/widgets/Timeline";
import ProgressBar from "../../components/widgets/ProgressBar";
import { getBuyer } from "../../js/api/buyersApi";
import { listAgentResponsesForBuyer } from "../../js/api/agentResponseApi";
import { deskTransactions } from "../../js/api/deskScope";
import { resolveClientFileId } from "../../js/api/filePacket";
import { preferenceProgress, statusLabel, formatNotesText, formatDate, prettyValue, BUYER_STEPS } from "../../js/utils/formatters";
import { useAgentScope } from "../../js/hooks/useAgentScope";
import { useDesk } from "../../js/hooks/useDesk";

export default function AgentClientDetail() {
  const { id } = useParams();
  const desk = useDesk();
  const { session, listAgentId, excludeAgentId } = useAgentScope();
  const shell = desk.variant === "broker" ? "broker" : "agent";
  const [client, setClient] = useState(null);
  const [responses, setResponses] = useState([]);
  const fileId = client
    ? resolveClientFileId({
        transactions: deskTransactions(session, listAgentId, excludeAgentId),
        buyer: client,
      })
    : "";

  useEffect(() => {
    getBuyer(id).then(setClient);
    listAgentResponsesForBuyer(id).then(setResponses);
  }, [id]);

  if (!client) {
    return (
      <AppLayout variant={shell}>
        <p>Loading client…</p>
      </AppLayout>
    );
  }

  const progress = preferenceProgress(client.preferences);
  const items = BUYER_STEPS.filter((step) => step.key !== "start" && step.key !== "complete").map((step) => {
    const value =
      client.preferences[
        step.key === "transaction" ? "transactionChecklist" : step.key === "tips" ? "tipsAcknowledged" : step.key
      ];
    const complete =
      typeof value === "boolean" ? value : typeof value === "string" ? Boolean(value) : Boolean(value && Object.keys(value).length);
    return { id: step.key, label: step.label, complete };
  });

  return (
    <AppLayout variant={shell}>
      <p>
        <Link to={desk.clients}>← All clients</Link>
      </p>
      <div className="page-header">
        <div>
          <h1>{client.name}</h1>
          <p className="muted">
            {client.email} · {client.phone}
          </p>
        </div>
        <span className="badge">{statusLabel(client.status)}</span>
      </div>
      <div className="two-col">
        <div className="card stack">
          <ProgressBar percent={progress.percent} label="DreamHome Checklist" />
          <Timeline items={items} />
        </div>
        <div className="card stack">
          <h2>Notes from the Buyer Checklist</h2>
          <p className="muted">These are the Special Notes the buyer wrote — not office Messages.</p>
          <p style={{ whiteSpace: "pre-wrap" }}>
            {formatNotesText(client.preferences.notes) || "No notes yet."}
          </p>
          <div className="row" style={{ gap: "0.65rem", flexWrap: "wrap" }}>
            {fileId ? (
              <Link to={desk.file(fileId)} className="primary-btn">
                Open File
              </Link>
            ) : null}
            <Link to={desk.messages} className="ghost-btn">
              Open messages
            </Link>
          </div>
        </div>
      </div>
      <section className="card stack" style={{ marginTop: "1rem" }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div>
            <h2>Property Responses</h2>
            <p className="muted">Walk listings against this buyer’s checklist.</p>
          </div>
          <Link to={`${desk.responseNew}?buyerId=${client.id}`} className="pill-btn start-cta">
            New property response
          </Link>
        </div>
        {responses.length ? (
          <ul className="stack" style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {responses.map((item) => (
              <li key={item.id}>
                <Link to={desk.responsePath(item.id, "location")}>
                  {[item.property?.address, item.property?.city].filter(Boolean).join(", ") || "Untitled listing"}
                </Link>
                <div className="muted">
                  {item.summary?.recommendation ? prettyValue(item.summary.recommendation) : "In progress"}
                  {item.updatedAt ? ` · ${formatDate(item.updatedAt)}` : ""}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No property responses yet for this buyer.</p>
        )}
      </section>
    </AppLayout>
  );
}
